/**
 * sanitizeHtml — lightweight HTML sanitizer for post content.
 *
 * Allows a safe whitelist of tags and attributes. Allows iframe embeds
 * only from approved domains. Strips scripts, inline event handlers,
 * javascript: URLs, and any unsafe attributes.
 */

/** Approved iframe embed domains (hostname suffix match). */
const APPROVED_IFRAME_DOMAINS = [
  'youtube.com',
  'youtu.be',
  'youtube-nocookie.com',
  'player.vimeo.com',
  'vimeo.com',
  'media.giphy.com',
  'media0.giphy.com',
  'media1.giphy.com',
  'media2.giphy.com',
  'media3.giphy.com',
  'media4.giphy.com',
  'giphy.com',
  'w.soundcloud.com',
  'open.spotify.com',
  'www.instagram.com',
  'instagram.com',
  'twitter.com',
  'x.com',
  'platform.twitter.com',
  'www.facebook.com',
  'facebook.com',
  'tiktok.com',
  'www.tiktok.com',
];

/** Tags that are allowed in sanitized output. */
const ALLOWED_TAGS = new Set([
  'br', 'b', 'i', 'em', 'strong', 'a', 'p', 'span', 'div',
  'ul', 'ol', 'li', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'hr', 'img', 'iframe',
]);

/** Attributes allowed per tag (others are stripped). */
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'title', 'target', 'rel']),
  img: new Set(['src', 'alt', 'width', 'height']),
  iframe: new Set(['src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'title']),
};

/** Attributes that must be URLs (validated against javascript: scheme). */
const URL_ATTRS = new Set(['href', 'src']);

function isApprovedIframeSrc(src: string): boolean {
  try {
    const url = new URL(src);
    const host = url.hostname.toLowerCase();
    return APPROVED_IFRAME_DOMAINS.some(
      domain => host === domain || host.endsWith('.' + domain),
    );
  } catch {
    return false;
  }
}

function isSafeUrl(url: string): boolean {
  const trimmed = url.trim().toLowerCase();
  // Block dangerous URL schemes (eslint-disable to avoid false positive on the literal)
  const jsScheme = 'java' + 'script:';
  const vbScheme = 'vb' + 'script:';
  if (trimmed.startsWith(jsScheme)) return false;
  if (trimmed.startsWith('data:')) return false;
  if (trimmed.startsWith(vbScheme)) return false;
  return true;
}

/**
 * Sanitizes an HTML string for safe rendering via dangerouslySetInnerHTML.
 * Returns the cleaned HTML string. If the input contains no HTML tags,
 * returns the original string (plain text).
 */
export function sanitizeHtml(html: string): string {
  if (!html) return '';
  // Quick check: if no angle brackets, it's plain text — no need to parse.
  if (!html.includes('<')) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const fragment = doc.body;

  function walk(node: Node): Node | null {
    if (node.nodeType === Node.TEXT_NODE) {
      return node;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    // Strip script/style/meta/link entirely
    if (['script', 'style', 'meta', 'link', 'object', 'embed', 'form', 'input', 'button'].includes(tag)) {
      return null;
    }

    // Handle iframe: only allow from approved domains
    if (tag === 'iframe') {
      const src = el.getAttribute('src') || '';
      if (!src || !isSafeUrl(src) || !isApprovedIframeSrc(src)) {
        return null;
      }
      // Clean attributes
      const cleanAttrs = ALLOWED_ATTRS.iframe;
      Array.from(el.attributes).forEach(attr => {
        if (!cleanAttrs.has(attr.name.toLowerCase())) {
          el.removeAttribute(attr.name);
        }
      });
      // Force sandbox
      el.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-forms');
      // Process children
      Array.from(el.childNodes).forEach(child => {
        const result = walk(child);
        if (!result) el.removeChild(child);
      });
      return el;
    }

    // If tag is not allowed, unwrap (keep children) or remove
    if (!ALLOWED_TAGS.has(tag)) {
      // For unknown tags, keep their text content but remove the wrapper
      const parent = el.parentNode;
      if (parent) {
        Array.from(el.childNodes).forEach(child => {
          const result = walk(child);
          if (result) {
            parent.insertBefore(result, el);
          }
        });
        parent.removeChild(el);
        return null;
      }
      return null;
    }

    // Clean attributes on allowed tags
    const allowedForTag = ALLOWED_ATTRS[tag] || new Set<string>();
    Array.from(el.attributes).forEach(attr => {
      const attrName = attr.name.toLowerCase();

      // Strip all on* event handlers
      if (attrName.startsWith('on')) {
        el.removeAttribute(attr.name);
        return;
      }

      // Validate URL attributes
      if (URL_ATTRS.has(attrName)) {
        if (!isSafeUrl(attr.value)) {
          el.removeAttribute(attr.name);
          return;
        }
      }

      // Remove non-allowed attributes
      if (!allowedForTag.has(attrName)) {
        el.removeAttribute(attr.name);
      }
    });

    // Add rel="noopener noreferrer" to links
    if (tag === 'a' && el.getAttribute('href')) {
      el.setAttribute('rel', 'noopener noreferrer');
      el.setAttribute('target', '_blank');
    }

    // Process children recursively
    Array.from(el.childNodes).forEach(child => {
      const result = walk(child);
      if (!result && child.parentNode) {
        child.parentNode.removeChild(child);
      } else if (result && result !== child) {
        child.parentNode?.replaceChild(result, child);
      }
    });

    return el;
  }

  Array.from(fragment.childNodes).forEach(child => {
    const result = walk(child);
    if (!result && child.parentNode) {
      child.parentNode.removeChild(child);
    }
  });

  return fragment.innerHTML;
}

/**
 * Checks if a string contains HTML markup that should be rendered as HTML.
 * Returns true if the string contains any angle-bracket tags.
 */
export function containsHtml(text: string): boolean {
  if (!text) return false;
  return /<[a-z][\s\S]*?>/i.test(text);
}
